const BASE_URL = "https://prizo.io";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-default.png`;

// Aggregate rating for the Prizo app, used in SoftwareApplication structured data.
// IMPORTANT: these MUST reflect genuine, publicly verifiable reviews (e.g. the
// Shopify App Store listing). Fabricated ratings violate Google's structured-data
// guidelines and can trigger a manual action. Set to `null` until real review
// data exists, in which case the rating is simply omitted from the schema.
const APP_AGGREGATE_RATING: {
  ratingValue: string;
  reviewCount: string;
} | null = {
  // Real Shopify App Store rating: 6 reviews, all 5★ → 5.0 overall.
  // Source: https://apps.shopify.com/prizo
  ratingValue: "5",
  reviewCount: "6",
};

// Real reviews from the Shopify App Store listing (verbatim). Used to emit
// Review structured data on the homepage. Keep these accurate to the source.
export const APP_REVIEWS: {
  author: string;
  body: string;
  rating: string;
  date: string;
}[] = [
  {
    author: "FNIRSI",
    body: "Prizo is a very practical app and has helped me increase my sales significantly. The support team responds quickly, and any feature issues I encountered were solved right away. Highly recommended!",
    rating: "5",
    date: "2025-11-21",
  },
  {
    author: "Mythic Apparel Tees",
    body: "Prizo is not just a great company, they are great humans that designed a tool to produce results. Gamify your thank you page and watch the results roll in — our own company has had an impressive lift in conversions. Don't hesitate. Give this app a try.",
    rating: "5",
    date: "2025-08-04",
  },
];

function fullUrl(path: string): string {
  return path.startsWith("http") ? path : `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export interface SeoMetaOptions {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: string;
}

export function getSeoMeta(options: SeoMetaOptions) {
  const { title, description, url, image = DEFAULT_OG_IMAGE, type = "website" } = options;
  const fullUrl = url.startsWith("http") ? url : `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: fullUrl },
    { property: "og:type", content: type },
    { property: "og:image", content: image.startsWith("http") ? image : `${BASE_URL}${image}` },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { tagName: "link", rel: "canonical", href: fullUrl },
  ];
}

export interface FaqItem {
  question: string;
  answer: string;
}

/** Build FAQPage JSON-LD for pages with FAQ sections. */
export function getFaqPageJsonLd(faq: FaqItem[], pageUrl: string): object {
  const url = fullUrl(pageUrl);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
    url,
  };
}

/**
 * Build BreadcrumbList JSON-LD. `items` are ordered root → current page.
 * BreadcrumbList is one of the few schema types that still renders a SERP
 * feature (the breadcrumb trail in place of the raw URL).
 */
export function getBreadcrumbListJsonLd(items: { name: string; path: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: fullUrl(item.path),
    })),
  };
}

/** Build SoftwareApplication JSON-LD for game pages. */
export function getSoftwareApplicationJsonLd(
  name: string,
  description: string,
  pageUrl: string,
  applicationCategory = "Game"
): object {
  const url = fullUrl(pageUrl);
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url,
    applicationCategory,
    operatingSystem: "Web (Shopify)",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    ...(APP_AGGREGATE_RATING
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: APP_AGGREGATE_RATING.ratingValue,
            reviewCount: APP_AGGREGATE_RATING.reviewCount,
          },
        }
      : {}),
  };
}

/** Build BlogPosting JSON-LD for blog articles. */
export function getArticleJsonLd(opts: {
  headline: string;
  description: string;
  pageUrl: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
}): object {
  const url = fullUrl(opts.pageUrl);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: opts.headline,
    description: opts.description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: { "@type": "Organization", name: opts.author ?? "Prizo", url: BASE_URL },
    publisher: {
      "@type": "Organization",
      name: "Prizo",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/logo-dark.png` },
    },
    image: opts.image ?? DEFAULT_OG_IMAGE,
  };
}

/** Build Organization JSON-LD for the brand (use on the homepage). */
export function getOrganizationJsonLd(): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Prizo",
    url: BASE_URL,
    logo: `${BASE_URL}/logo-dark.png`,
    description:
      "Prizo is a Shopify app that adds gamified popups and post-purchase games — spin the wheel, scratch cards, and more — to help merchants capture emails, reduce cart abandonment, and increase repeat sales.",
    sameAs: ["https://apps.shopify.com/prizo"],
  };
}

/**
 * Build the homepage SoftwareApplication JSON-LD, enriched with real Review
 * objects (in addition to the aggregateRating from getSoftwareApplicationJsonLd).
 * Reviews belong on the app entity, which on the homepage represents all of Prizo.
 */
export function getAppWithReviewsJsonLd(): object {
  const base = getSoftwareApplicationJsonLd(
    "Prizo",
    "Shopify app for gamified popups and post-purchase games — spin the wheel, scratch cards, plinko and more — to capture emails, reduce cart abandonment, and increase repeat sales.",
    "/",
    "BusinessApplication",
  ) as Record<string, unknown>;

  return {
    ...base,
    review: APP_REVIEWS.map((r) => ({
      "@type": "Review",
      author: { "@type": "Organization", name: r.author },
      datePublished: r.date,
      reviewBody: r.body,
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: "5",
        worstRating: "1",
      },
    })),
  };
}
